import React, { useEffect, useRef } from 'react';
import DataManager from '@americanexpress/data-manager';
import { useDispatch, useSelector } from 'react-redux';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Helmet } from 'react-helmet';
import { IntlProvider, FormattedMessage } from 'react-intl';
import gsap from 'gsap';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import {
  actions,
  rafThrottle,
  isBreakpointUp,
  getCookie,
  getUrlQueryParams,
  DEFAULT_LOCALE,
} from '@americanexpress/amex-connect-core';

import childRoutes from '../childRoutes';
import { reducer } from '../ducks';
import { loadLocale } from '../locale';
import { MODULE_NAME } from '../constants';
import { initialDDLProviderProps } from '../ddlProviderProps';
import '../styles/index.scss';

// without this line, ScrollToPlugin may get dropped by your bundler...
gsap.registerPlugin(ScrollToPlugin);

export const AmexConnectRoot = ({
  children, locale, location, languageData,
}) => {
  const dispatch = useDispatch();
  const prevInnerHeightRef = useRef();
  const amexConnectState = useSelector((state) => {
    const windowState = state.getIn(['modules', MODULE_NAME, 'rootReducer', 'window']);
    return {
      innerHeight: windowState.get('innerHeight'),
      amexUrl: state.getIn(['config', 'amexUrl']),
      tenantUrl: state.getIn(['config', 'tenantUrl']),
    };
  });
  const setWindowSize = () => dispatch(actions.setWindowSize());
  const reloadLocale = (urlLocale) => dispatch(loadLocale(urlLocale));

  const { innerHeight } = amexConnectState;

  useEffect(() => {
    if (location) {
      const { locale: urlLocale } = getUrlQueryParams(location.search);
      if (urlLocale && locale !== urlLocale) {
        reloadLocale(urlLocale);
      }
    }
    window.addEventListener('resize', rafThrottle(setWindowSize));
    setTimeout(() => {
      setWindowSize();
    }, 100);
  }, [locale, location, reloadLocale, setWindowSize]);

  useEffect(() => {
    if (!isBreakpointUp('md')) {
      /*
      Don't change on mobile devices, because safari triggers resize when url bar shrinks.
      Still changes on orientation change, because both dimensions change at once.
      */
      if (prevInnerHeightRef.current !== innerHeight) {
        document.documentElement.style.setProperty('--fix100vh', `${innerHeight}px`);
      }
    }
    prevInnerHeightRef.current = innerHeight;
  }, [innerHeight]);

  const mycaGUID = getCookie('blueboxpublic') || '1234';
  const { ddlProviderProps, reactlyticsProps } = getAmexDataManagerConfig();
  const updatedInitialDDLProps = {
    ...initialDDLProviderProps,
    ddl: { user: { publicGuid: mycaGUID } },
  };
  const updatedDDLProps = {
    ...ddlProviderProps,
    initialDDL: updatedInitialDDLProps,
  };

  return (
    <DataManager
      ddlProviderProps={updatedDDLProps}
      reactlyticsProps={reactlyticsProps}
    >
      <AmexDDLRegister />
      <Adobe version="^1.0.0" />
      <DDLRegister name="device.channel" value="Desktop" />
      <SharedScript
        name="one-identity-session"
        version="^1.0.0"
        windowCheck="AmexSession"
      />
      <IntlProvider locale={locale} messages={languageData}>
        <FormattedMessage id="connect.title">
          {(title) => (
            <Helmet>
              <html lang={locale} />
              <meta name="viewport" content="width=device-width, initial-scale=1" />
              <title>{title}</title>
              <link rel="stylesheet" href="https://www.aexp-static.com/cdaas/one/statics/axp-dls/6.11.0/package/dist/6.11.0/styles/dls.min.css" />
            </Helmet>
          )}
        </FormattedMessage>
        {children}
      </IntlProvider>
    </DataManager>
  );
};

AmexConnectRoot.childRoutes = childRoutes;

export const loadModuleData = ({ store: { dispatch } }) => dispatch(
  loadLanguagePack(MODULE_NAME, { fallbackLocale: DEFAULT_LOCALE })
);

AmexConnectRoot.holocron = {
  name: MODULE_NAME,
  reducer,
  loadModuleData,
};

AmexConnectRoot.propTypes = {
  children: PropTypes.node.isRequired,
  locale: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  languageData: PropTypes.shape({}),
};

/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- excluding it
  AmexConnectRoot.appConfig = require('../appConfig').default;
}

const hocChain = compose(oneAppModuleWrapper(MODULE_NAME));

export default hocChain(AmexConnectRoot);
